import { useMemo } from "react";
import { ExplainerPanel } from "./MaintenanceUi";
import SensorHistoryChart from "./SensorHistoryChart";
import { EDGE_MARGIN, hasValue } from "./cupGrinderSensorHistory";
import {
  DEFAULT_SENSITIVITY,
  sensitivityOf,
  thresholdOf,
  thresholdTable,
} from "./cupGrinderSensitivity";
import { SensitivityPicker, useSensitivityDraft } from "./SensitivityPicker";

// Bakım → "Fincan ve Değirmen Sensörleri" yanındaki açıklama paneli
// (sözleşme v1.1 §5 "Açıklama grafiği", #132): seçili sensörün canlı
// değeri, eşiği, var/yok bölgeleri, hassasiyetin eşiği nereye koyduğu,
// son kayıtların zaman grafiği ve kısa anlatım.

// Eksen aralığı — değer bunun dışındaysa işaretçi uca yapışır, sayı yazılır.
const AXIS_RANGE = {
  cup: { lo: -600, hi: 150 },
  grinder: { lo: -260, hi: 80 },
};

const TEXT = {
  cup: { present: "Fincan var", absent: "Fincan yok" },
  grinder: { present: "Ağızda fincan var", absent: "Ağızda fincan yok" },
};

export const cupsOf = (data) => (Array.isArray(data?.cups) ? data.cups : []);

/** Yükten seçili sensörü bul ("cup-<n>" ya da "grinder"). */
const pickSensor = (data, id) =>
  id === "grinder"
    ? data?.grinder || null
    : cupsOf(data).find((c) => `cup-${c?.cup}` === id) || null;

const fallbackOf = (data, kind) =>
  kind === "grinder" ? data?.grinderThreshold : data?.cupThreshold;

/** Yatay değer ekseni: var/yok bölgeleri, eşik, canlı değer, 10 hassasiyet işareti. */
export function SensorAxis({
  kind,
  value,
  threshold,
  sensitivity,
  savedThreshold = null,
}) {
  const range = AXIS_RANGE[kind];
  const lo = Math.min(range.lo, threshold - 2 * EDGE_MARGIN);
  const hi = Math.max(range.hi, threshold + 2 * EDGE_MARGIN);
  const clamp = (v) => Math.min(hi, Math.max(lo, v));
  const pct = (v) => ((clamp(v) - lo) / (hi - lo)) * 100;
  const thrPct = pct(threshold);
  const edgeLeft = pct(threshold - EDGE_MARGIN);
  const edgeRight = pct(threshold + EDGE_MARGIN);
  const hasLive = typeof value === "number" && Number.isFinite(value);
  const valuePct = hasLive ? pct(value) : null;
  const table = thresholdTable(kind);
  const text = TEXT[kind];

  return (
    <div data-testid="sensor-axis" className="flex flex-col gap-1">
      {/* Canlı değer etiketi */}
      <div className="relative h-5">
        {hasLive ? (
          <span
            data-testid="axis-value"
            className="absolute -translate-x-1/2 whitespace-nowrap rounded bg-main px-1.5 text-xs font-SemiBold tabular-nums text-white"
            style={{ left: `${Math.min(92, Math.max(8, valuePct))}%` }}
          >
            Şu an {value}
          </span>
        ) : (
          <span className="text-xs text-gray-400">Canlı değer yok</span>
        )}
      </div>

      {/* Eksen: solda "var" (büyük negatif), sağda "yok" (0 / artı) */}
      <div className="relative h-10 overflow-hidden rounded-md border border-gray-200">
        <div
          className="absolute inset-y-0 left-0 bg-emerald-100"
          style={{ width: `${thrPct}%` }}
        />
        <div
          className="absolute inset-y-0 right-0 bg-gray-100"
          style={{ left: `${thrPct}%` }}
        />
        <div
          className="absolute inset-y-0 bg-amber-100/80"
          style={{ left: `${edgeLeft}%`, width: `${edgeRight - edgeLeft}%` }}
          title={`Sınırda: eşiğe ${EDGE_MARGIN}'den yakın`}
        />
        <span className="absolute left-1.5 top-1 text-[10px] font-SemiBold text-emerald-800">
          {text.present}
        </span>
        <span className="absolute right-1.5 top-1 text-[10px] font-SemiBold text-gray-500">
          {text.absent}
        </span>
        {/* 0 çizgisi */}
        <div
          className="absolute inset-y-0 w-px bg-gray-400"
          style={{ left: `${pct(0)}%` }}
        />
        {/* Eşik çizgisi */}
        <div
          data-testid="axis-threshold"
          className="absolute inset-y-0 w-0.5 -translate-x-1/2 bg-amber-500"
          style={{ left: `${thrPct}%` }}
        />
        {/* Önizlemede kayıtlı eşik ince kesikli çizgi */}
        {savedThreshold !== null && savedThreshold !== threshold && (
          <div
            data-testid="axis-saved-threshold"
            title={`Kayıtlı eşik ${savedThreshold}`}
            className="absolute inset-y-0 w-0 -translate-x-1/2 border-l border-dashed border-gray-500"
            style={{ left: `${pct(savedThreshold)}%` }}
          />
        )}
        {hasLive && (
          <div
            className="absolute bottom-0 top-3 w-1.5 -translate-x-1/2 rounded-sm bg-main"
            style={{ left: `${valuePct}%` }}
          />
        )}
      </div>

      {/* Hassasiyet 1-10'un eşik konumları */}
      <div className="relative h-3" aria-hidden="true">
        {table.map((t) => (
          <span
            key={t.sensitivity}
            className={`absolute top-0 -translate-x-1/2 rounded-full ${
              t.sensitivity === sensitivity
                ? "h-3 w-1 bg-amber-500"
                : "h-2 w-px bg-gray-400"
            }`}
            style={{ left: `${pct(t.threshold)}%` }}
          />
        ))}
      </div>

      <div className="relative h-4 text-[10px] tabular-nums text-gray-500">
        <span className="absolute left-0">{lo}</span>
        <span
          className="absolute -translate-x-1/2 font-SemiBold text-amber-700 whitespace-nowrap"
          style={{ left: `${Math.min(85, Math.max(15, thrPct))}%` }}
        >
          {savedThreshold !== null && savedThreshold !== threshold
            ? `önizleme eşik ${threshold}`
            : `eşik ${threshold}`}
        </span>
        <span className="absolute right-0">+{hi}</span>
      </div>
    </div>
  );
}

/**
 * Tek sensörün açıklaması — sensörü içinde bulunduğu kutu seçer (fincan
 * kutusunda seçili göz, değirmen kutusunda "grinder").
 * @param {object} props
 * @param {object|null} props.live son canlı yük (yoksa null)
 * @param {{ receivedAt: number, data: object }[]} props.history son kayıtlar
 * @param {string} props.sensorId "cup-<n>" ya da "grinder"
 */
export default function SensorExplainer({
  live,
  history = [],
  sensorId,
  onSaveSensitivity,
}) {
  const current = sensorId;
  const kind = current === "grinder" ? "grinder" : "cup";

  const sensor = pickSensor(live, current);
  const saved = sensor ? sensitivityOf(sensor) : null;
  const picker = useSensitivityDraft(saved, onSaveSensitivity);
  const sensitivity = picker.selected ?? DEFAULT_SENSITIVITY;
  const savedThreshold = thresholdOf(kind, sensor, fallbackOf(live, kind));
  // Seçim önizlemesi: eşik seçilen seviyenin tablo değeri.
  const preview =
    picker.draft !== null
      ? thresholdTable(kind)[picker.draft - 1].threshold
      : null;
  const threshold = preview ?? savedThreshold;
  const value = hasValue(sensor) ? sensor.diff : null;

  const points = useMemo(
    () =>
      history.map((e) => {
        const s = pickSensor(e.data, current);
        return {
          at: e.receivedAt,
          value: hasValue(s) ? s.diff : null,
          threshold: preview ?? thresholdOf(kind, s, fallbackOf(e.data, kind)),
        };
      }),
    [history, current, kind, preview],
  );
  const chartable = points.filter((p) => p.value !== null).length >= 2;
  const noun = kind === "grinder" ? "değirmen ağzında fincan" : "fincan";

  return (
    <ExplainerPanel
      testId="sensor-explainer"
      right={
        kind === "cup" ? (
          <span className="rounded-md bg-gray-100 px-2 py-1 text-xs font-SemiBold text-gray-600">
            Göz {current.replace("cup-", "")}
          </span>
        ) : null
      }
    >
      <SensorAxis
        kind={kind}
        value={value}
        threshold={threshold}
        sensitivity={sensitivity}
        savedThreshold={preview !== null ? savedThreshold : null}
      />

      <SensitivityPicker
        title="Hassasiyet → eşik"
        unit="eşik"
        table={thresholdTable(kind)}
        saved={saved}
        fallback={DEFAULT_SENSITIVITY}
        picker={picker}
        canWrite={typeof onSaveSensitivity === "function"}
      />

      <section className="flex flex-col gap-1">
        <h4 className="text-xs font-SemiBold text-gray-600">
          Son kayıtlar ({points.length})
        </h4>
        {chartable ? (
          <SensorHistoryChart points={points} />
        ) : (
          <p className="text-xs text-gray-400">
            Grafik için izlemeyi başlatın; en az iki kayıt gerekir.
          </p>
        )}
      </section>

      <ul className="flex flex-col gap-1.5 text-xs text-gray-600 list-disc pl-4">
        <li>
          <span className="font-SemiBold text-gray-800">Değer:</span> sensörün
          ışığı açıkken ve kapalıyken ölçtüğü yansımanın farkı. Ortam ışığı bu
          farkta kendini götürür.
        </li>
        <li>
          <span className="font-SemiBold text-gray-800">Neden eksi:</span>{" "}
          önünde fincan varken ışık fincandan geri yansır, değer eksiye iner.
          Önü boşken 0 civarında ya da artıda kalır.
        </li>
        <li>
          <span className="font-SemiBold text-gray-800">Eşik:</span> kart bu
          değerin altını "{noun} var" sayar. Karar için 10 ardışık ölçüm aynı
          tarafta olmalı; anlık sıçrama kararı değiştirmez.
        </li>
        <li>
          <span className="font-SemiBold text-gray-800">
            Hassasiyeti artırınca
          </span>{" "}
          eşik sıfıra yaklaşır; zayıf yansıma da "var" sayılır. Koyu ya da kirli
          fincan, sensöre uzak duran fincan "yok" görünüyorsa artırın.
        </li>
        <li>
          <span className="font-SemiBold text-gray-800">Azaltınca</span> eşik
          eksiye uzaklaşır; daha güçlü yansıma gerekir. Ortam ışığı ya da
          parlama yüzünden boş yerde "var" deniyorsa azaltın.
        </li>
        <li>
          <span className="font-SemiBold text-gray-800">5 = fabrika</span> ayarı
          (fincan -250, değirmen -110).
        </li>
        <li>
          <span className="font-SemiBold text-gray-800">Öneri:</span> fincan
          varken ve yokken değer eşikten en az ~{EDGE_MARGIN} uzak dursun.
          Turuncu "sınırda" kalıyorsa hassasiyeti bir-iki adım değiştirin.
        </li>
      </ul>
    </ExplainerPanel>
  );
}
